import React, { useEffect, useState, useRef } from 'react';
import api from '../../api';
import TomSelect from '../../components/common/TomSelect';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import SuccessModal from '../../components/common/SuccessModal';
import FailModal from '../../components/common/FailModal';
import { usePagination } from '../../hooks/usePagination';
import { useSelection } from '../../hooks/useSelection';
import { validateField } from '../../utils/validation';
import { GenericView, SearchInput } from '../../components/common/table';
import { useAuth } from '../../contexts/AuthProvider';

const NaceCode = () => {
  // Actions dropdown refs for manual Bootstrap Dropdown initialization
  const actionsToggleRef = useRef(null);
  const actionsContainerRef = useRef(null);
  const actionsDropdownInstanceRef = useRef(null);
  const [isActionsOpen, setIsActionsOpen] = useState(false);

  useEffect(() => {
    const toggleEl = actionsToggleRef.current;
    const containerEl = actionsContainerRef.current;
    if (!toggleEl || !containerEl) return;
    if (typeof window !== 'undefined' && window.bootstrap?.Dropdown) {
      const instance = new window.bootstrap.Dropdown(toggleEl, { autoClose: true });
      actionsDropdownInstanceRef.current = instance;
      const onShow = () => setIsActionsOpen(true);
      const onHide = () => setIsActionsOpen(false);
      containerEl.addEventListener('show.bs.dropdown', onShow);
      containerEl.addEventListener('hide.bs.dropdown', onHide);
      return () => {
        containerEl.removeEventListener('show.bs.dropdown', onShow);
        containerEl.removeEventListener('hide.bs.dropdown', onHide);
        try { instance.dispose(); } catch {}
        actionsDropdownInstanceRef.current = null;
      };
    }
  }, []);

  // Permission system
  const { user, can, capabilitiesLoading } = useAuth();
  const canNaceCode = (key) => can(key, 'naceCode');

  // Pagination
  const pagination = usePagination(10);

  // Search
  const [search, setSearch] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');

  const handleSearchInputChange = (value) => {
    setSearch(value);
    setSearchTerm(value);
  };

  const handleSearchSubmit = () => {
    setAppliedSearchTerm(searchTerm);
    pagination.setPage(0);
  };

  // Selection
  const {
    selectedIds,
    setSelectedIds,
    handleCheckboxClick,
    handleSelectAll
  } = useSelection();

  // Date filter
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // Data and form state
  const [naceCodes, setNaceCodes] = useState([]);
  const [standards, setStandards] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [formData, setFormData] = useState({ naceCode: '', naceDivision: '', eaCodeId: '', standards: [] });
  const [editingId, setEditingId] = useState(null);
  const [errors, setErrors] = useState({});
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [confirmAction, setConfirmAction] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showFailModal, setShowFailModal] = useState(false);
  const [failMessage, setFailMessage] = useState('');

  // Fetch audit types
  const fetchNaceCodes = async (searchValue = search) => {
    try {
      setIsLoading(true);
      const response = await api.get(`/md/nace-codes`, {
        params: {
          page: pagination.page,
          size: pagination.size,
          search: searchValue,
          from: fromDate,
          to: toDate
        }
      });
      // Accept both `items` and `content` as possible keys for data array
      const dataArr = response.data.items || response.data.content || [];
      setNaceCodes(dataArr);
      pagination.setTotalPages(response.data.totalPages);
      pagination.setTotal(response.data.totalElements);
      pagination.setCurrentPageItems(response.data.content.length);
    } catch (error) {
      setNaceCodes([]);
    } finally {
      setIsLoading(false);
    }
  };
  const fetchSectors = async () => {
    try {
      const response = await api.get('/md/sectors2');
      setSectors(response.data);
    } catch (error) {
      setSectors([]);
    }
  };

  // Fetch Standards
  const fetchStandards = async () => {
    try {
      const response = await api.get('/md/standards/select-options');
      setStandards(response.data);
    } catch (error) {
      setStandards([]);
    }
  };

  useEffect(() => {
    fetchNaceCodes(appliedSearchTerm);
    fetchStandards();
    fetchSectors();
  }, [pagination.page, pagination.size, fromDate, toDate, appliedSearchTerm]);

  // Open modal for create
  const handleCreate = () => {
    if (!canNaceCode('create')) { setFailMessage('You do not have permission to create.'); setShowFailModal(true); return; }
    setFormData({ naceCode: '', naceDivision: '', eaCodeId: '', standards: [] });
    setEditingId(null);
    setErrors({});
    if (window.bootstrap && window.bootstrap.Modal) {
      const modal = new window.bootstrap.Modal(document.getElementById('modal-nace-code'));
      modal.show();
    }
  };

  const handleEdit = (item) => {
    if (!canNaceCode('edit')) { setFailMessage('You do not have permission to edit.'); setShowFailModal(true); return; }
    setFormData({
      naceCode: item.naceCode,
      naceDivision: item.naceDivision,
      eaCodeId: item.eaCode && item.eaCode.id != null ? String(item.eaCode.id) : '',
      standards: item.standards?.map(std => String(std.value ?? std.id ?? std)) || []
    });
    setEditingId(item.id);
    setErrors({});
    setTimeout(() => {
      if (window.bootstrap && window.bootstrap.Modal) {
        const modal = new window.bootstrap.Modal(document.getElementById('modal-nace-code'));
        modal.show();
      }
    }, 100); // 100ms delay to allow state to propagate
  };

  // Real-time validation
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    let error = null;
    if (name === 'naceCode') {
      error = validateField('naceCode', value, { required: true });
    } else if (name === 'naceDivision') {
      error = validateField('naceDivision', value, { required: true });
    }
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  // TomSelect change handler
  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    let error = null;
    if (name === 'standards') {
      error = value.length === 0 ? 'At least one Standard is required' : null;
    } else if (name === 'eaCodeId') {
      error = !value ? 'Sector is required' : null;
    }
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  // Unified handleSubmit for both create and edit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    try {
      const payload = {
        naceCode: formData.naceCode,
        naceDivision: formData.naceDivision,
        eaCodeId: formData.eaCodeId,
        standards: formData.standards
      };

      if (editingId) {
        await api.put(`/md/nace-codes/${editingId}`, { ...payload, id: editingId });
        setSuccessMessage('NACE Code has been successfully updated.');
      } else {
        await api.post('/md/nace-codes', payload);
        setSuccessMessage('NACE Code has been successfully added.');
      }
      setShowSuccessModal(true);
      setEditingId(null);
      setFormData({ naceCode: '', naceDivision: '', eaCodeId: '', standards: [] });
      setErrors({});
      await fetchNaceCodes();
      const modal = document.getElementById('modal-nace-code');
      const bootstrapModal = window.bootstrap?.Modal.getInstance(modal);
      if (bootstrapModal) bootstrapModal.hide();
    } catch (error) {
      console.error('Error saving NACE code:', error);
      let errorMessage = 'Failed to save NACE code.';
      let technicalDetails = '';

      if (error.response) {
        switch (error.response.status) {
          case 409:
            errorMessage = 'A NACE code with this code already exists.';
            technicalDetails = `Duplicate entry for NACE code: "${formData.naceCode}"`;
            break;
          case 400:
            errorMessage = 'Invalid data provided.';
            technicalDetails = error.response.data.message || 'The server rejected the request due to invalid data';
            break;
          case 403:
            errorMessage = 'You do not have permission to perform this action.';
            technicalDetails = 'Required role or permission is missing for this operation';
            break;
          case 404:
            errorMessage = 'NACE code not found.';
            technicalDetails = 'The requested item may have been deleted or does not exist';
            break;
          case 500:
            errorMessage = 'Server encountered an error while processing the request.';
            technicalDetails = error.response.data.message || 'Internal server error occurred';
            break;
          default:
            errorMessage = 'An error occurred while saving the NACE code.';
            technicalDetails = `Server responded with status: ${error.response.status}`;
        }
      } else if (error.request) {
        errorMessage = 'Unable to connect to the server.';
        technicalDetails = 'Network error or server is not responding';
      } else {
        errorMessage = 'An unexpected error occurred.';
        technicalDetails = error.message;
      }

      setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
      setShowFailModal(true);
    }
  };

  // Edit selected (only works for single selection)
  const editSelected = () => {
    if (Array.isArray(selectedIds) && selectedIds.length !== 1) {
      setSuccessMessage('Please select exactly one NACE Code to edit');
      setShowSuccessModal(true);
      return;
    }
    const selectedId = Array.isArray(selectedIds) ? selectedIds[0] : null;
    const selectedItem = Array.isArray(naceCodes) && naceCodes.find(item => item.id === selectedId);
    if (selectedItem) {
      handleEdit(selectedItem);
    }
  };

  // Delete selected (bulk)
  const deleteSelected = () => {
    if (!canNaceCode('delete')) { setFailMessage('You do not have permission to delete.'); setShowFailModal(true); return; }
    if (selectedIds.length === 0) {
      setFailMessage('Please select at least one NACE code to delete.\n\nTechnical Details: No items selected for deletion');
      setShowFailModal(true);
      return;
    }
    setConfirmAction(() => async () => {
      try {
        await api.delete('/md/nace-codes', { data: selectedIds });
        setSelectedIds([]);
        fetchNaceCodes();
        setShowDeleteConfirmation(false);
        setSuccessMessage('Selected NACE codes deleted successfully');
        setShowSuccessModal(true);
      } catch (error) {
        let errorMessage = 'Failed to delete NACE codes.';
        let technicalDetails = '';

        if (error.response) {
          switch (error.response.status) {
            case 403:
              errorMessage = 'You do not have permission to delete these items.';
              technicalDetails = 'Required role or permission is missing for deletion';
              break;
            case 400:
              errorMessage = 'Invalid delete request.';
              technicalDetails = error.response.data.message || 'The server rejected the deletion request';
              break;
            case 404:
              errorMessage = 'One or more items not found.';
              technicalDetails = 'Some items may have been already deleted';
              break;
            case 500:
              errorMessage = 'Server error while deleting items.';
              technicalDetails = error.response.data.message || 'Internal server error during deletion';
              break;
            default:
              errorMessage = 'An error occurred while deleting the items.';
              technicalDetails = `Server responded with status: ${error.response.status}`;
          }
        } else if (error.request) {
          errorMessage = 'Unable to connect to the server.';
          technicalDetails = 'Network error or server is not responding';
        } else {
          errorMessage = 'An unexpected error occurred during deletion.';
          technicalDetails = error.message;
        }

        setShowDeleteConfirmation(false);
        setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
        setShowFailModal(true);
      }
    });
    setShowDeleteConfirmation(true);
  };

  // Modal cleanup
  useEffect(() => {
    const modal = document.getElementById('modal-nace-code');
    modal?.addEventListener('hidden.bs.modal', () => {
      setFormData({ naceCode: '', naceDivision: '', eaCodeId: '', standards: [] });
      setEditingId(null);
    });
    return () => {
      modal?.removeEventListener('hidden.bs.modal', () => { });
    };
  }, []);

  const validateForm = () => {
    let naceCodeError = validateField('naceCode', formData.naceCode, { required: true });
    let standardsError = formData.standards.length === 0 ? 'At least one Standard is required' : null;
    let naceDivisionError = validateField('naceDivision', formData.naceDivision, { required: true });
    let eaCodeIdError = !formData.eaCodeId ? 'Sector is required' : null;

    setErrors({
      naceCode: naceCodeError,
      standards: standardsError,
      naceDivision: naceDivisionError,
      eaCodeId: eaCodeIdError
    });

    if (naceCodeError || standardsError || naceDivisionError || eaCodeIdError) return false;
    return true;
  };

  const columns = [
    { key: 'naceCode', label: 'NACE Code', sortable: true },
    { key: 'naceDivision', label: 'NACE Division/Group/Class', sortable: true },
    { key: 'eaCode', label: 'EAC Code', sortable: false, render: (value) => value?.eaCode || '-' },
    { key: 'eaCode', label: 'Sector', sortable: false, render: (value) => value?.name || '-' },
    { key: 'standardNames', label: 'Standard', sortable: false, render: (value) => Array.isArray(value) ? value.join(', ') : (value || '-') },
    { key: 'cdate', label: 'Created Date', sortable: true, render: (value) => value ? new Date(value).toLocaleDateString() : '-' },
  ];

  return (
    <>
        {/* Page header */}
        <div className="page-header d-print-none">
          <div className="container-xl">
            <div className="row g-2 align-items-center">
              <div className="col">
                <h2 className="page-title">
                  NACE Code
                </h2>
              </div>
              {/* Page title actions */}
              <div className="col-auto ms-auto d-print-none">
                <div className="btn-list">
                  <a href="#" className="btn btn-primary d-none d-sm-inline-block"
                    onClick={handleCreate}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                      fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                      strokeLinejoin="round" className="icon icon-2">
                      <path d="M12 5l0 14" />
                      <path d="M5 12l14 0" />
                    </svg>
                    Create
                  </a>
                  <a href="#" className="btn btn-primary d-sm-none btn-icon" data-bs-toggle="modal"
                    data-bs-target="#modal-nace-code" aria-label="Create new report">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                      fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                      strokeLinejoin="round" className="icon icon-2">
                      <path d="M12 5l0 14" />
                      <path d="M5 12l14 0" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Page body */}
        <div className="page-body">
          <div className="container-xl">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <GenericView
                    data={naceCodes}
                    columns={columns}
                    isLoading={isLoading || capabilitiesLoading}
                    emptyStateMessage="No NACE Codes found"
                    checkboxSelection={true}
                    selectedRows={selectedIds}
                    onSelectRow={(itemId, isSelected) => {
                      if (isSelected) {
                        setSelectedIds([...selectedIds, itemId]);
                      } else {
                        setSelectedIds(selectedIds.filter(id => id !== itemId));
                      }
                    }}
                    onSelectAllRows={(itemIds) => {
                      if (Array.isArray(itemIds) && itemIds.length > 0) {
                        setSelectedIds([...new Set([...selectedIds, ...itemIds])]);
                      } else {
                        setSelectedIds([]);
                      }
                    }}
                    onEdit={canNaceCode('edit') ? (item) => {
                      setSelectedIds([item.id]);
                      handleEdit(item);
                    } : undefined}
                    onDelete={canNaceCode('delete') ? (item) => {
                      setSelectedIds([item.id]);
                      deleteSelected();
                    } : undefined}
                    searchComponent={
                      <div className="d-flex gap-2">
                        <SearchInput
                          value={searchTerm}
                          onChange={handleSearchInputChange}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleSearchSubmit();
                            }
                          }}
                          placeholder="Search..."
                          uniqueId="nace-code-search"
                        />
                        <button type="button" className="btn btn-primary" onClick={handleSearchSubmit}>
                          Search
                        </button>
                      </div>
                    }
                    actionButtons={
                      <div className="d-flex gap-2">
                        {(canNaceCode('edit') || canNaceCode('delete')) && (
                          <div ref={actionsContainerRef} className="dropdown">
                            <a
                              ref={actionsToggleRef}
                              href="#"
                              className="btn dropdown-toggle"
                              data-bs-toggle="dropdown"
                              aria-expanded={isActionsOpen}
                              onClick={(e) => {
                                e.preventDefault();
                                const inst = actionsDropdownInstanceRef.current;
                                if (inst?.toggle) inst.toggle();
                                else setIsActionsOpen(prev => !prev);
                              }}
                            >Actions</a>
                            <div className="dropdown-menu">
                              {canNaceCode('edit') && (
                                <a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); editSelected(); }}
                                  style={{ color: selectedIds.length === 1 ? "" : "#ccc", pointerEvents: selectedIds.length === 1 ? "auto" : "none" }}>
                                  Edit
                                </a>
                              )}
                              {canNaceCode('delete') && (
                                <a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); deleteSelected(); }}
                                  style={{ color: selectedIds.length ? "" : "#ccc", pointerEvents: selectedIds.length ? "auto" : "none" }}>
                                  Delete
                                </a>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    }
                    pagination={{
                      currentPage: pagination.page + 1,
                      totalRows: pagination.total,
                      rowsPerPage: pagination.size,
                      onPageChange: (page) => pagination.setPage(page - 1),
                      onRowsPerPageChange: (size) => {
                        pagination.setSize(size);
                        pagination.setPage(0);
                      }
                    }}
                    pageSizeOptions={[10, 25, 50, 100]}
                    uniqueId="nace-code-table"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Create and edit NACE Code Modal */}
        <div className="modal modal-blur fade" id="modal-nace-code" tabIndex="-1" role="dialog" aria-hidden="true">
          <div className="modal-dialog modal-lg" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingId ? 'Edit NACE Code' : 'Create NACE Code'}</h5>
                <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-lg-12">
                      <div className="mb-3">
                        <TomSelect
                          key={editingId || 'create'}
                          name="eaCodeId"
                          value={formData.eaCodeId ? String(formData.eaCodeId) : ''}
                          onChange={(name, value) => {
                            handleSelectChange(name, value ? String(value) : '');
                          }}
                          options={sectors.map(sector => ({ id: String(sector.value), name: sector.label }))}
                          placeholder="Select Sector"
                          label="Sector"
                          required={true}
                          error={errors.eaCodeId}
                          valueField="id"
                          labelField="name"
                          searchField="name"
                          multiple={false}
                        />
                      </div>
                    </div>
                    <div className="col-lg-12">
                      <div className="mb-3">
                        <label className="form-label required">NACE Code</label>
                        <input
                          type="text"
                          className={`form-control ${errors.naceCode ? 'is-invalid' : ''}`}
                          id="naceCode"
                          name="naceCode"
                          value={formData.naceCode}
                          onChange={handleInputChange}
                          placeholder="Enter nace code"
                        />
                        {errors.naceCode && <div className="invalid-feedback">{errors.naceCode}</div>}
                      </div>
                    </div>
                    <div className="col-lg-12">
                      <div className="mb-3">
                        <label className="form-label required">NACE Division/Group/Class</label>
                        <input
                          type="text"
                          className={`form-control ${errors.naceDivision ? 'is-invalid' : ''}`}
                          id="naceDivision"
                          name="naceDivision"
                          value={formData.naceDivision}
                          onChange={handleInputChange}
                          placeholder="Enter nace division"
                        />
                        {errors.naceDivision && <div className="invalid-feedback">{errors.naceDivision}</div>}
                      </div>
                    </div>
                    <div className="col-lg-12">
                      <div className="mb-3">
                        <TomSelect
                          key={editingId || 'create'}
                          name="standards"
                          value={formData.standards.map(String)}
                          onChange={(name, value) => {
                            // Always store as array of strings for consistency
                            const ids = Array.isArray(value) ? value.map(String) : [];
                            handleSelectChange(name, ids);
                          }}
                          options={standards.map(standard => ({ id: String(standard.value), name: standard.label }))}
                          placeholder="Select Standard"
                          label="Standard"
                          required={true}
                          error={errors.standards}
                          valueField="id"
                          labelField="name"
                          searchField="name"
                          multiple={true}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <a href="#" className="btn btn-link link-secondary btn-3" data-bs-dismiss="modal">Cancel</a>
                  <button type="submit" className="btn btn-primary ms-auto" id="createBtn">
                    {editingId ? 'Update' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      
      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={confirmAction || (() => { })}
        title="Delete Confirmation"
        message={selectedIds.length > 0
          ? "Are you sure you want to delete the selected NACE codes? This action cannot be undone."
          : "Are you sure you want to delete this NACE code? This action cannot be undone."}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Success"
        message={successMessage}
        buttonText="OK"
      />
      {/* Fail Modal */}
      <FailModal
        isOpen={showFailModal}
        onClose={() => setShowFailModal(false)}
        title="Operation Failed"
        message={failMessage}
        buttonText="Close"
      />
    </>
  );
};

export default NaceCode; 